import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMedium } from '@fortawesome/free-brands-svg-icons';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

const MEDIUM_PROFILE_URL = 'https://medium.com/@saayeeem';
const MEDIUM_RSS_URLS = [
  'https://api.allorigins.win/raw?url=https://medium.com/feed/@saayeeem',
  'https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@saayeeem'
];
const POSTS_PER_PAGE = 3;
const REQUEST_TIMEOUT_MS = 10000;

function fetchWithTimeout(url, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });
}

function stripHtml(html) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html || '';
  return tempDiv.textContent || tempDiv.innerText || '';
}

function isLikelyPostImage(url) {
  if (!url) {
    return false;
  }

  const normalized = url.toLowerCase();
  const blockedPatterns = [
    '/fit/c/150/150/',
    'avatar',
    'profile',
    'icon',
    '1*k39veo5wvejzhvrhj0f5ja'
  ];

  return !blockedPatterns.some((pattern) => normalized.includes(pattern));
}

function extractBestThumbnail(item, content) {
  const enclosureUrl = item.querySelector('enclosure')?.getAttribute('url') || '';
  const mediaThumbnailUrl = item.querySelector('media\\:thumbnail')?.getAttribute('url') || '';
  const mediaContentUrl = item.querySelector('media\\:content')?.getAttribute('url') || '';
  const metadataCandidates = [enclosureUrl, mediaThumbnailUrl, mediaContentUrl].filter(Boolean);

  const imgRegex = /<img[^>]+src=["']([^"'>]+)["']/gi;
  const htmlCandidates = [...content.matchAll(imgRegex)].map((match) => match[1]);
  const allCandidates = [...metadataCandidates, ...htmlCandidates];

  const bestCandidate = allCandidates.find((candidate) => isLikelyPostImage(candidate));
  return bestCandidate || allCandidates[0] || '';
}

function parseXmlPosts(xmlText) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
  const parseError = xmlDoc.querySelector('parsererror');

  if (parseError) {
    throw new Error('Failed to parse RSS feed');
  }

  const items = Array.from(xmlDoc.querySelectorAll('item')).map((item) => {
    const title = item.querySelector('title')?.textContent || '';
    const link = item.querySelector('link')?.textContent || '';
    const guid = item.querySelector('guid')?.textContent || link;
    const pubDate = item.querySelector('pubDate')?.textContent || '';
    const content = item.getElementsByTagName('content:encoded')[0]?.textContent ||
      item.querySelector('description')?.textContent ||
      '';
    const thumbnail = extractBestThumbnail(item, content);

    const plainText = stripHtml(content);

    return {
      title,
      link,
      guid,
      thumbnail,
      description: plainText.substring(0, 150),
      pubDate
    };
  });

  return items.filter((item) => item.title && item.link);
}

function parseRss2JsonPosts(data) {
  if (!data || data.status !== 'ok' || !Array.isArray(data.items)) {
    throw new Error('Invalid rss2json response');
  }

  return data.items.map((item) => {
    const descriptionHtml = item.description || item.content || '';
    const thumbnailCandidates = [
      item.thumbnail || '',
      item.enclosure?.link || ''
    ].filter(Boolean);
    const fromDescription = [...descriptionHtml.matchAll(/<img[^>]+src=["']([^"'>]+)["']/gi)].map((match) => match[1]);
    const allCandidates = [...thumbnailCandidates, ...fromDescription];
    const thumbnail = allCandidates.find((candidate) => isLikelyPostImage(candidate)) || allCandidates[0] || '';

    return {
      title: item.title || '',
      link: item.link || '',
      guid: item.guid || item.link || item.title,
      thumbnail,
      description: stripHtml(descriptionHtml).substring(0, 150),
      pubDate: item.pubDate || ''
    };
  }).filter((item) => item.title && item.link);
}

async function fetchMediumPosts() {
  let lastError = null;

  for (const url of MEDIUM_RSS_URLS) {
    try {
      const response = await fetchWithTimeout(url, REQUEST_TIMEOUT_MS);

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      if (url.includes('rss2json')) {
        const data = await response.json();
        const items = parseRss2JsonPosts(data);
        if (items.length > 0) {
          return items;
        }
      } else {
        const xmlText = await response.text();
        const items = parseXmlPosts(xmlText);
        if (items.length > 0) {
          return items;
        }
      }
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('Unable to load Medium posts');
}

function MediumPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let isMounted = true;

    fetchMediumPosts()
      .then((items) => {
        if (!isMounted) {
          return;
        }

        if (items.length > 0) {
          setPosts(items);
          setError(null);
        } else {
          setError('No posts found.');
        }
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        void err;
        setError('Failed to load latest Medium posts right now.');
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const paginatedPosts = posts.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Optional: scroll to top of section if needed
    // document.getElementById('blog').scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) return (
    <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
      <div className="animate-pulse">Loading Medium posts...</div>
    </div>
  );

  if (error) return (
    <div className="max-w-5xl mx-auto" id="blog">
      <div className="flex items-center gap-3 mb-8">
        <FontAwesomeIcon icon={faMedium} className="text-2xl" style={{ color: 'var(--text-primary)' }} />
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Latest from Medium</h2>
      </div>
      <div
        className="rounded-xl border p-6 text-center"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border)'
        }}
      >
        <div className="flex justify-center mb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--bg-surface)' }}
          >
            <FontAwesomeIcon icon={faMedium} className="text-xl" style={{ color: 'var(--text-primary)' }} />
          </div>
        </div>
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
          {error} You can still view all articles on my Medium profile.
        </p>
        <a
          href={MEDIUM_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold"
          style={{
            backgroundColor: 'var(--primary)',
            color: '#ffffff',
            textDecoration: 'none'
          }}
        >
          <FontAwesomeIcon icon={faMedium} />
          Visit My Medium Profile
          <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
        </a>
      </div>
    </div>
  );

  return (
    <div className="medium-posts max-w-5xl mx-auto" id="blog">
      <div className="flex items-center gap-3 mb-8">
        <FontAwesomeIcon icon={faMedium} className="text-2xl" style={{ color: 'var(--text-primary)' }} />
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Latest from Medium</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {paginatedPosts.map((post) => (
          <a
            key={post.guid}
            href={post.link}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col h-full rounded-lg border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border)',
              textDecoration: 'none'
            }}
          >
            {/* Image Container */}
            <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-800">
              <img
                src={post.thumbnail || 'https://cdn-images-1.medium.com/fit/c/150/150/1*k39vEO5WvEjzHVRhJ0F5jA.jpeg'}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://cdn-images-1.medium.com/fit/c/150/150/1*k39vEO5WvEjzHVRhJ0F5jA.jpeg';
                }}
              />
              <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                Read on Medium <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1" />
              </div>
            </div>

            {/* Content */}
            <div className="p-5 flex flex-col flex-grow">
              <h5
                className="text-lg font-bold mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors"
                style={{ color: 'var(--text-primary)' }}
              >
                {post.title}
              </h5>
              <p
                className="text-sm leading-relaxed line-clamp-3 mb-4 flex-grow"
                style={{ color: 'var(--text-secondary)' }}
              >
                {post.description ? `${post.description.slice(0, 120)}...` : 'Read the full article on Medium.'}
              </p>

              <div className="flex items-center justify-between mt-auto pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                  {post.pubDate
                    ? new Date(post.pubDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                    : 'Recent post'}
                </span>
                <span className="text-xs font-semibold text-blue-600 group-hover:underline">
                  Read Article
                </span>
              </div>
            </div>
          </a>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-10 gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)'
            }}
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => handlePageChange(i + 1)}
              className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                currentPage === i + 1
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              style={currentPage !== i + 1 ? {
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)'
              } : {}}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default MediumPosts;
